import {
  VisibleInCargoTrackingTransportStatus,
  cargoTrackingStrings,
  trackingDataMeta,
} from '@data/cargoTracking';
import { file, recordToKV } from '@utils/types';
import * as t from 'io-ts';
import { pick, pipe, map, maxBy, filter } from 'remeda';
import * as h from 'tyrann-io';

export const trackingDetailItem = t.type({
  key: t.string,
  time: t.string, // ISO-format time string
  place: h.omittable(t.string), // Example: SHANGHAI/CHINA
  file: h.omittable(h.array(t.string)), // URL to related document
  note: h.omittable(t.string),
  invoice_id: h.omittable(t.number),
});

export type TrackingDetailItem = t.TypeOf<typeof trackingDetailItem>;

const cargoCompanies = [
  t.literal('西濃運輸'),
  t.literal('エスラインギフ S LINE'),
  t.literal('福山通運'),
  t.literal('トナミ運輸'),
  t.literal('第一貨物'),
  t.literal('久留米運送'),
  t.literal('山陽自動車運送'),
  t.literal('備後通運'),
  t.literal('名鉄運輸'),
  t.literal('佐川急便'),
  t.literal('日本通運'),
  t.literal('日通トランスポート'),
] as const;

export const allCargoCompanies = cargoCompanies.map((c) => c.value);

export const cargoCoop = t.union([
  ...cargoCompanies,
  t.literal('チャータートラック'),
  t.literal('コンテナ'),
]);

export const cargoCoops = cargoCoop.types.map((l) => l.value);

export const isCargoCoop = (name: string): name is CargoCoop => {
  return cargoCoops.includes(name as any);
};

export const isCargoCompany = (name: string): name is CargoCoop => {
  return cargoCompanies.some((n) => n.value === name);
};

export type CargoCoop = t.TypeOf<typeof cargoCoop>;

export const domesticTrackingDetailItem = t.type({
  key: t.string,
  time: t.string, // ISO-format time string
  coop_name: h.omittable(t.string), // Name of the transporter, loosen to accept of names
  coop_tracking_no: h.omittable(t.string),
  car_no: h.omittable(t.string),
  car_driver: h.omittable(t.string),
  note: h.omittable(t.string),
  file: h.omittable(h.array(t.string)), // URL to related document
});

export type DomesticTrackingDetailItem = t.TypeOf<typeof domesticTrackingDetailItem>;

export const trackingDataDict = t.type({
  [cargoTrackingStrings.bookingConfirmed]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.prealertGenerated]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.labelGeneration]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.chinaFactoryPickup]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.exportMovedIn]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.exportClearance]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.exportMovedOut]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.deliveryGuidance]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.importArrived]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.arriveAtTheWarehouse]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.importCustomsDeclaration]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.declaration1]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.declaration2]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.declaration3]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.customsCheck]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.importDeclared]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.domesticShipped]: h.omittable(t.array(domesticTrackingDetailItem)),
  [cargoTrackingStrings.done]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.cancelled]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.seaWaybillGenerated]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.arrivalNoticeGenerated]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.invoiceIssuance]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.invoiceIssuanceCompleted]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.invoicePaid]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.paymentSlipIssuance]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.paymentSlipIssuanceCompleted]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.paymentSlipPaid]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.customsInstructionConfirmed]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.rcepInstructionConfirmed]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.deliveryInstructionConfirmed]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.individualInsurance]: h.omittable(t.array(trackingDetailItem)),
  [cargoTrackingStrings.driverInfoAndDeliveryInvoice]: h.omittable(
    t.array(domesticTrackingDetailItem),
  ),
  [cargoTrackingStrings.realTimeAccount]: h.omittable(t.array(trackingDetailItem)),
});

export const transportStatus = t.union([
  t.literal(cargoTrackingStrings.bookingConfirmed),
  t.literal(cargoTrackingStrings.prealertGenerated),
  t.literal(cargoTrackingStrings.labelGeneration),
  t.literal(cargoTrackingStrings.deliveryGuidance), // obsolete
  t.literal(cargoTrackingStrings.exportMovedIn),
  t.literal(cargoTrackingStrings.chinaFactoryPickup),
  t.literal(cargoTrackingStrings.exportClearance),
  t.literal(cargoTrackingStrings.exportMovedOut),
  t.literal(cargoTrackingStrings.importArrived),
  t.literal(cargoTrackingStrings.arriveAtTheWarehouse),
  t.literal(cargoTrackingStrings.importCustomsDeclaration),
  t.literal(cargoTrackingStrings.importDeclared),
  t.literal(cargoTrackingStrings.domesticShipped),
  t.literal(cargoTrackingStrings.driverInfoAndDeliveryInvoice),
  t.literal(cargoTrackingStrings.done),
  t.literal(cargoTrackingStrings.cancelled),
  t.literal(cargoTrackingStrings.seaWaybillGenerated),
  t.literal(cargoTrackingStrings.arrivalNoticeGenerated),
  t.literal(cargoTrackingStrings.invoiceIssuance),
  t.literal(cargoTrackingStrings.invoiceIssuanceCompleted),
  t.literal(cargoTrackingStrings.invoicePaid),
  t.literal(cargoTrackingStrings.paymentSlipIssuance),
  t.literal(cargoTrackingStrings.paymentSlipIssuanceCompleted),
  t.literal(cargoTrackingStrings.paymentSlipPaid),
  t.literal(cargoTrackingStrings.customsInstructionConfirmed),
  t.literal(cargoTrackingStrings.rcepInstructionConfirmed),
  t.literal(cargoTrackingStrings.deliveryInstructionConfirmed),
  t.literal(cargoTrackingStrings.individualInsurance),
  t.literal(cargoTrackingStrings.realTimeAccount),
  t.literal(cargoTrackingStrings.customsCheck),
  t.literal(cargoTrackingStrings.declaration1),
  t.literal(cargoTrackingStrings.declaration2),
  t.literal(cargoTrackingStrings.declaration3),
]);

export const allTransportStatus = transportStatus.types.map((v) => v.value);

export type TransportStatus = t.TypeOf<typeof transportStatus>;

export const domesticTransportStatus = t.union([
  t.literal(cargoTrackingStrings.domesticShipped),
  t.literal(cargoTrackingStrings.done),
]);

export type DomesticTransportStatus = t.TypeOf<typeof domesticTransportStatus>;

export const trackingData = t.type({
  id: t.string,
  track_no: t.string,
  data: trackingDataDict,
  coop_name: t.string,
  coop_track_no: t.string,
  car_no: t.string,
  car_driver: t.string,
  transport_status: transportStatus,
  export_moved_in_status: t.boolean,
  created_at: t.string,
  updated_at: t.string,
});

export type TrackingData = t.TypeOf<typeof trackingData>;

export const trackingDataBody = t.type({
  transport_status: h.omittable(transportStatus),
  place: h.omittable(t.string),
  time: h.omittable(t.string),
  coop_name: h.omittable(t.string),
  coop_tracking_no: h.omittable(t.string),
  car_no: h.omittable(t.string),
  car_driver: h.omittable(t.string),
  file: h.omittable(t.array(file())),
  note: h.omittable(t.string),
  key: h.omittable(t.string),
});

export type TrackingDataDeleteQuery = t.TypeOf<typeof trackingDataDeleteQuery>;

export const trackingDataDeleteQuery = t.type({
  key: t.string,
  transport_status: transportStatus,
});

export class TrackingDataHelper {
  constructor(public data: TrackingData) {}

  static getVisibleInCargoTrackingTransportStatus(): VisibleInCargoTrackingTransportStatus[] {
    return Object.entries(trackingDataMeta)
      .filter(([_, v]) => v.visibleInCargoTracking)
      .map(([k]) => k) as VisibleInCargoTrackingTransportStatus[];
  }

  getLastVisibleInCargoTrackingTransportStatus() {
    const filtered = pick(
      this.data.data,
      TrackingDataHelper.getVisibleInCargoTrackingTransportStatus(),
    );

    return pipe(
      recordToKV(filtered),
      filter(([_, v]) => Boolean(v)),
      map(
        ([k, v]) =>
          [
            k,
            pipe(
              (v ?? []) as TrackingDetailItem[],
              maxBy((status) => new Date(status.time).getTime()),
            ) as TrackingDetailItem | undefined,
          ] as const,
      ),
      maxBy(([_, v]) => (v ? new Date(v.time).getTime() : 0)),
    );
  }
}
